import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { VendorService } from '../../vendor-portal/vendor.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormUtils } from '../../utilities/FormUtils/FormUtils';
import { PhoneUtils } from '../../utilities/PhoneUtils/PhoneUtils';
import { EmailValidationService } from '../../services/email-validation.service';

@Component({
  selector: 'app-edit-customer',
  templateUrl: './edit-customer.component.html',
  styleUrls: ['./edit-customer.component.css']
})
export class EditCustomerComponent implements OnInit {

  customer: any = {};
  form: FormGroup;
  errorMessage: string = '';
  phoneErrorMessage: string = '';
  isSubmitting: boolean = false;
  phonesLoading = false;
  private nextPhoneKey = 1;
  /** Editable phone rows; first filled valid number is primary. Stable keys keep input focus. */
  phoneInputs: Array<{ key: number; value: string }> = [{ key: 0, value: '' }];

  constructor(
    public dialogRef: MatDialogRef<EditCustomerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private vendor: VendorService,
    private emailValidationService: EmailValidationService
  ) {
    this.customer = data || {};
    this.form = new FormGroup({
      id: new FormControl(this.customer.savedid, [Validators.required]),
      name: new FormControl(this.customer.name, [Validators.required]),
      email: new FormControl(this.customer.email, [this.emailValidationService.validateEmail.bind(this.emailValidationService)])
    });
    this.phoneInputs = this.toPhoneInputs(
      this.normalizePhones(this.customer.phones, this.customer.phone)
    );
  }

  ngOnInit() {
    this.loadPhones();
  }

  trackByPhoneKey(index: number, row: { key: number; value: string }): number {
    return row.key;
  }

  private extractPhonesList(res: any): any[] | null {
    if (!res) {
      return null;
    }
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res.phones)) {
      return res.phones;
    }
    if (res.data) {
      if (Array.isArray(res.data)) {
        return res.data;
      }
      if (Array.isArray(res.data.phones)) {
        return res.data.phones;
      }
    }
    return null;
  }

  private normalizePhones(phones: any, fallbackPhone?: string) {
    const list = this.extractPhonesList(phones);
    if (list && list.length) {
      const normalized = list.map((p) => ({
        id: p && p.id,
        phone: PhoneUtils.toTenDigitPhone(typeof p === 'string' ? p : (p && p.phone)),
        is_primary: !!(p && p.is_primary)
      })).filter((p) => !!p.phone);

      // Keep primary first for display / save.
      normalized.sort((a, b) => {
        if (a.is_primary === b.is_primary) {
          return 0;
        }
        return a.is_primary ? -1 : 1;
      });
      return normalized;
    }
    const primary = PhoneUtils.toTenDigitPhone(fallbackPhone);
    return primary ? [{ phone: primary, is_primary: true }] : [];
  }

  private toPhoneInputs(phones: Array<{ phone: string }>): Array<{ key: number; value: string }> {
    if (phones && phones.length) {
      return phones.map((p) => ({
        key: this.nextPhoneKey++,
        value: p.phone || ''
      }));
    }
    return [{ key: this.nextPhoneKey++, value: '' }];
  }

  loadPhones() {
    if (!this.customer.savedid) {
      return;
    }
    this.phonesLoading = true;
    this.vendor.getCustomerPhones(this.customer.savedid).then((res) => {
      const list = this.extractPhonesList(res);
      // Only replace local phones when the API returns a real phones array.
      if (list && list.length) {
        this.phoneInputs = this.toPhoneInputs(
          this.normalizePhones(list, this.customer.phone)
        );
      }
      this.phonesLoading = false;
    }).catch((e) => {
      console.error(e);
      this.phonesLoading = false;
    });
  }

  addPhone() {
    this.phoneErrorMessage = '';
    this.phoneInputs = this.phoneInputs.concat([{
      key: this.nextPhoneKey++,
      value: ''
    }]);
  }

  removePhone(index: number) {
    if (this.phoneInputs.length <= 1) {
      return;
    }
    this.phoneErrorMessage = '';
    this.phoneInputs = this.phoneInputs.filter((_, i) => i !== index);
  }

  makePrimary(index: number) {
    if (index <= 0 || index >= this.phoneInputs.length) {
      return;
    }
    this.phoneErrorMessage = '';
    const selected = this.phoneInputs[index];
    const rest = this.phoneInputs.filter((_, i) => i !== index);
    this.phoneInputs = [selected].concat(rest);
  }

  private collectPhones(): string[] {
    const seen = {};
    const phones = [];
    this.phoneInputs.forEach((row) => {
      const normalized = PhoneUtils.toTenDigitPhone(row && row.value);
      if (normalized && !seen[normalized]) {
        seen[normalized] = true;
        phones.push(normalized);
      }
    });
    return phones;
  }

  close() {
    this.dialogRef.close(false);
  }

  getClass(name) {
    if (this.isFieldInvalid(name)) {
      return 'form-control round-input error-border';
    } else {
      return 'form-control round-input';
    }
  }

  isFieldInvalid(name: string): boolean {
    var field = this.form.get(name);
    if (name == 'email') {
      if (field.getError('invalidEmail') && field.touched) {
        if (field.value == '' || !field.value) {
          return false;
        } else {
          return true;
        }
      }
    }
    if (field.invalid && field.touched) {
      return true;
    }
    return false;
  }

  getFieldErrorMessage(name: string): string {
    var field = this.form.get(name);
    if (field.invalid && field.touched) {
      if (field.errors['required']) {
        return `${name.charAt(0).toUpperCase() + name.slice(1)} is required.`;
      }
      if (field.errors['invalidEmail']) {
        return this.emailValidationService.getEmailErrorMessage();
      }
    }
    return '';
  }

  private extractErrorMessage(e: any, fallback: string): string {
    try {
      if (e && e._body) {
        const body = typeof e._body === 'string' ? JSON.parse(e._body) : e._body;
        if (body && body.message) {
          return body.message;
        }
      }
      if (e && e.error && e.error.message) {
        return e.error.message;
      }
      if (e && e.message) {
        return e.message;
      }
    } catch (err) {
      // ignore parse errors
    }
    return fallback;
  }

  save() {
    FormUtils.markFormGroupTouched(this.form);
    this.phoneErrorMessage = '';

    const phoneNumbers = this.collectPhones();
    if (!phoneNumbers.length) {
      this.phoneErrorMessage = 'At least one valid phone number is required.';
      return;
    }

    if (this.form.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      const form = this.form.getRawValue();
      this.vendor.updateCustomer({
        id: form.id,
        name: form.name,
        email: form.email,
        phone: phoneNumbers[0],
        phones: phoneNumbers
      }).then((res) => {
        this.isSubmitting = false;
        if (res && res.status !== false) {
          this.dialogRef.close(true);
        } else {
          this.errorMessage = (res && res.message) ? res.message : 'Failed to update customer. Please try again.';
        }
      }).catch(e => {
        this.isSubmitting = false;
        console.error('Update customer error:', e);
        this.errorMessage = this.extractErrorMessage(e, 'Failed to update customer. Please try again.');
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin-dashboard/edit-customer/edit-customer.component.ts